import { useAuth } from "@clerk/clerk-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { withAttributionHandoff } from "@/client/analytics/attribution";
import {
  firstRoundHomeUrl,
  movedToFirstRoundUrl,
  toFirstRoundAppUrl,
} from "@/client/utils/firstRoundAppUrl";

/**
 * The First Round marketing hosts only serve "/" and the industry pages. The server 301s any other
 * path to theresume.io on a full page load; this covers in-app navigation (e.g. a <Link> to
 * /careers), sending it to the same path on theresume.io, where Clerk and the app actually run.
 */
export const LeaveMarketingHost = () => {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    window.location.replace(toFirstRoundAppUrl(`${pathname}${search}${hash}`));
  }, [pathname, search, hash]);

  return null;
};

/**
 * Pages that moved to firstround.com.au. On production theresume.io this sends in-app navigation
 * there (the server already 301s full loads); on staging and localhost it renders the page.
 */
export const MovedToFirstRound = ({ children }: { children: ReactNode }) => {
  const { pathname, search, hash } = useLocation();
  const target = movedToFirstRoundUrl(pathname);

  useEffect(() => {
    if (target) window.location.replace(withAttributionHandoff(`${target}${search}${hash}`));
  }, [target, search, hash]);

  return target ? null : children;
};

/** Where theResume's old /pricing links go now that theResume sells the First Round plans. */
export const pricingRedirectTarget = (signedIn: boolean, hostname?: string) =>
  signedIn ? "/dashboard?plans=open" : `${firstRoundHomeUrl(hostname)}#pricing`;

/**
 * /pricing: signed in, the dashboard with the plan dialog open; signed out, the plans on
 * firstround.com.au (the in-app First Round page on staging and localhost). Old links and emails
 * keep working (A14).
 */
export const PricingRedirect = () => {
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (isLoaded && !isSignedIn) window.location.replace(pricingRedirectTarget(false));
  }, [isLoaded, isSignedIn]);

  if (isLoaded && isSignedIn) return <Navigate to={pricingRedirectTarget(true)} replace />;
  return null;
};
